import { useClickOutside } from '@hooks/useClickOutside';
import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { useLeagueSportsbook } from '@hooks/useLeagueSportsbook';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  LogOut,
  Mail,
  Palette,
  Radio,
  Settings,
  Shield,
} from 'lucide-react';
import { type ReactElement, useCallback, useRef, useState } from 'react';
import { getThemeList } from '../../config/themes';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { HeaderEssentialActions } from './HeaderEssentialActions';
import { HeaderLeagueSelector } from './HeaderLeagueSelector';
import { SportsbookSelector } from './SportsbookSelector';

interface UserMenuProps {
  onOpenSettings: () => void;
  onNavigateAdmin?: () => void;
}

export function UserMenu({ onOpenSettings, onNavigateAdmin }: UserMenuProps): ReactElement {
  const { user, logout, loading } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [showThemes, setShowThemes] = useState(false);
  const { themeId, setTheme } = useTheme();
  const { canUseTheme } = useFeatureAccess();
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setShowThemes(false);
  }, []);
  useClickOutside(menuRef, closeMenu, showMenu);

  const currentLeagueKey = useCurrentLeagueKey();
  const { sportsbook, setSportsbook } = useLeagueSportsbook(currentLeagueKey);

  if (loading) {
    return <div className="w-8 h-8 rounded-full bg-theme-surface animate-pulse" />;
  }

  return (
    <div className="flex items-center gap-1 sm:gap-2 min-w-0">
      {/* Always visible: essential action icons */}
      <HeaderEssentialActions />

      {/* Desktop only: selectors */}
      <div className="hidden @6xl:flex items-center gap-2">
        <HeaderLeagueSelector />
        <SportsbookSelector
          selected={sportsbook}
          onChange={setSportsbook}
          leagueKey={currentLeagueKey}
        />
      </div>

      {/* Avatar + dropdown menu */}
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          data-testid="user-menu-button"
          onClick={() => setShowMenu(!showMenu)}
          className="flex items-center gap-2 px-2 py-1.5 bg-theme-surface hover:bg-theme-surface-hover rounded-lg transition-colors shrink-0"
        >
          <div className="w-7 h-7 rounded-full bg-theme-primary-fill flex items-center justify-center text-theme-on-primary-fill font-medium shrink-0 text-sm">
            {user?.displayName[0].toUpperCase()}
          </div>
          <span className="text-sm text-theme-text-secondary hidden @[90rem]:inline truncate max-w-30">
            {user?.displayName}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-theme-text-tertiary shrink-0 hidden sm:block" />
        </button>

        {showMenu && (
          <div className="fixed right-2 sm:absolute sm:right-0 mt-2 w-48 bg-theme-surface rounded-lg shadow-lg py-1 z-20 border border-theme-border">
            <div className="px-4 py-2 border-b border-theme-border">
              <p className="text-sm font-medium text-theme-text truncate">{user?.displayName}</p>
              <p className="text-xs text-theme-text-tertiary truncate">{user?.email}</p>
            </div>
            {/* Live Scores */}
            <button
              type="button"
              onClick={() => {
                useGlobalShortcutsStore.getState().openBoxScore('', [], currentLeagueKey);
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-surface-hover transition-colors flex items-center gap-2"
            >
              <Radio className="w-4 h-4" />
              Live Scores
            </button>
            {user?.role === 'admin' && onNavigateAdmin && (
              <button
                type="button"
                onClick={() => {
                  onNavigateAdmin();
                  setShowMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-surface-hover transition-colors flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Admin
              </button>
            )}
            <div className="my-1 border-t border-theme-border/50" />
            <button
              type="button"
              onClick={() => {
                onOpenSettings();
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-surface-hover transition-colors flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
            <button
              type="button"
              onClick={() => setShowThemes(!showThemes)}
              className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-surface-hover transition-colors flex items-center gap-2"
            >
              <Palette className="w-4 h-4" />
              <span className="flex-1">Theme</span>
              <ChevronRight
                className={`w-3 h-3 text-theme-text-tertiary transition-transform ${showThemes ? 'rotate-90' : ''}`}
              />
            </button>
            {showThemes && (
              <div className="max-h-48 overflow-y-auto border-t border-theme-border/50">
                {getThemeList().map((theme) => {
                  const locked = !canUseTheme(theme.id);
                  return (
                    <button
                      type="button"
                      key={theme.id}
                      onClick={() => {
                        if (locked) return;
                        setTheme(theme.id);
                        setShowThemes(false);
                        setShowMenu(false);
                      }}
                      className={`w-full text-left px-4 py-1.5 text-xs transition-colors flex items-center gap-2 ${
                        locked
                          ? 'text-theme-text-tertiary opacity-50 cursor-not-allowed'
                          : themeId === theme.id
                            ? 'bg-theme-surface-active text-theme-text'
                            : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                      }`}
                    >
                      <div className="flex gap-0.5">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: theme.colors.primary }}
                        />
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: theme.colors.secondary }}
                        />
                      </div>
                      <span className="flex-1 truncate">{theme.name}</span>
                      {themeId === theme.id && (
                        <CheckCircle className="w-3 h-3 text-theme-success shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                window.open('/faq', '_blank');
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-surface-hover transition-colors flex items-center gap-2"
            >
              <HelpCircle className="w-4 h-4" />
              Help & FAQ
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = '/contact';
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-theme-text-secondary hover:bg-theme-surface-hover transition-colors flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </button>
            <button
              type="button"
              data-testid="user-menu-sign-out"
              onClick={() => {
                logout();
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-theme-danger hover:bg-theme-surface-hover transition-colors flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
